import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faUpload, faFileExcel, faFileCsv, faDownload, 
  faSpinner, faCheck, faExclamationTriangle, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { importSuppliers, downloadSupplierTemplate } from '../../services/api';

interface SupplierImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

const SupplierImportModal: React.FC<SupplierImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // **File-Handler**
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file: File) => {
    const allowedTypes = ['.xlsx', '.xls', '.csv'];
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      alert('Nur Excel (.xlsx, .xls) und CSV (.csv) Dateien sind erlaubt');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Datei ist zu groß (max. 5MB)');
      return;
    }
    
    setImportFile(file);
    setImportResults(null);
  };

  // **Drag & Drop Handler**
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  // **Import-Handler**
  const handleImportSubmit = async () => {
    if (!importFile) return;

    try {
      setImporting(true);
      const response = await importSuppliers(importFile);
      setImportResults(response.data.results);
      onImportComplete();
    } catch (error) {
      console.error('Import error:', error);
      alert('Import fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setImporting(false);
    }
  };

  // **Template-Download mit Authentication**
  const downloadTemplate = async (format: 'xlsx' | 'csv') => {
    try {
      const blob = await downloadSupplierTemplate(format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lieferanten-template.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      alert('Fehler beim Herunterladen der Vorlage. Bitte versuchen Sie es erneut.');
    }
  };

  // **Modal schließen**
  const handleClose = () => {
    setImportFile(null);
    setImportResults(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.01 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.99 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.99 }}
          transition={{ duration: 0.02 }}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* **HEADER WIE VERSIONSHISTORY** */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-base font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faUpload} className="text-green-500 mr-2 text-sm" />
                Lieferanten importieren
              </h2>
              <p className="text-xs text-gray-600 mt-1">
                Excel- oder CSV-Datei mit Lieferantendaten importieren
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </button>
          </div>

          {/* **CONTENT** */}
          <div className="p-4 max-h-[calc(90vh-140px)] overflow-y-auto">
            {!importResults ? (
              <div className="space-y-4">
                {/* **TEMPLATE-DOWNLOAD** */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                    <FontAwesomeIcon icon={faDownload} className="mr-2" />
                    Vorlagen herunterladen
                  </h4>
                  <p className="text-xs text-blue-700 mb-3">
                    Laden Sie eine Vorlage herunter, um das richtige Format zu verwenden:
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => downloadTemplate('xlsx')}
                      className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 font-medium transition-colors"
                    >
                      <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                      Excel-Vorlage
                    </button>
                    <button
                      onClick={() => downloadTemplate('csv')}
                      className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium transition-colors"
                    >
                      <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                      CSV-Vorlage
                    </button>
                  </div>
                </div>

                {/* **DATEI-UPLOAD** */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Datei auswählen
                  </label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                      dragActive 
                        ? 'border-blue-400 bg-blue-50' 
                        : importFile 
                          ? 'border-green-400 bg-green-50' 
                          : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <FontAwesomeIcon 
                      icon={importFile ? faCheck : faUpload} 
                      className={`text-2xl mb-2 ${
                        importFile ? 'text-green-600' : 'text-gray-400'
                      }`} 
                    />
                    <p className="text-sm text-gray-700 mb-1">
                      {importFile ? importFile.name : 'Datei hierher ziehen oder klicken'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Unterstützte Formate: .xlsx, .xls, .csv (max. 5MB)
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* **FORMAT-INFO** */}
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2 flex items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                    Erforderliche Spalten
                  </h4>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    <li>• <strong>Lieferantenname</strong> (erforderlich)</li>
                    <li>• <strong>LieferantenGLN</strong> (optional, 14 Ziffern)</li>
                    <li>• <strong>Email-Adresse</strong> (erforderlich)</li>
                    <li>• <strong>EK-Bereiche:</strong> EK0, EK1, EK2, EK3, EK4, EK5, EK6, EK7, EK8, EK9, EKV, EKD, EKW, EKG, EKSG (optional)</li>
                  </ul>
                </div>
              </div>
            ) : (
              /* **IMPORT-ERGEBNISSE** */
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                  <h4 className="text-sm font-medium text-green-900 mb-2 flex items-center">
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    Import abgeschlossen
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-700">{importResults.imported}</div>
                      <div className="text-green-600">Neu erstellt</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-700">{importResults.updated}</div>
                      <div className="text-blue-600">Aktualisiert</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-red-700">{importResults.skipped}</div>
                      <div className="text-red-600">Übersprungen</div>
                    </div>
                  </div>
                </div>

                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <h4 className="text-sm font-medium text-red-900 mb-2 flex items-center">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                      Fehler ({importResults.errors.length})
                    </h4>
                    <div className="max-h-32 overflow-y-auto">
                      {importResults.errors.map((error: string, index: number) => (
                        <div key={index} className="text-xs text-red-700 mb-1">
                          • {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* **FOOTER WIE VERSIONSHISTORY** */}
          <div className="flex gap-3 p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors text-sm"
            >
              {importResults ? 'Schließen' : 'Abbrechen'}
            </button>
            {!importResults && (
              <button
                onClick={handleImportSubmit}
                disabled={!importFile || importing}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors text-sm"
              >
                {importing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Importiere...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUpload} className="mr-2" />
                    Importieren
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SupplierImportModal;